<template>
  <v-app>
    <v-app-bar ref="appBar" color="app-bar">
      <v-app-bar-nav-icon @click.stop="drawer = !drawer" class="d-flex d-lg-none"></v-app-bar-nav-icon>
      <v-toolbar-title>
        <!-- Vuetify wraps the title in a placeholder div that isn't flex: this row centres the chip on the lockup -->
        <div class="d-flex align-center">
          <!-- Brand lockup (docs/BRAND.md): logo D with "Québec" in white and "Eau Vive" in Écume. The name follows
               the domain or the visitor's choice (services/SiteName.service.js) -->
          <!-- text-white: keeps the link out of the plain-link colours of app.scss -->
          <router-link
            :to="{ name: 'accueil' }"
            class="brand-lockup text-white"
            :aria-label="`${siteName.label}, accueil`"
          >
            <span class="brand-lockup__mark">
              <img src="@/assets/brand/logo.svg" alt="" class="brand-lockup__logo" width="40" height="40" />
              <!-- Phones: no room for the chip next to the name, so the testing environment is tagged on the logo -->
              <span v-if="showTestingEnvironmentChip" class="env-tag d-sm-none" title="Testing environment">Test</span>
            </span>
            <span class="brand-lockup__name"
              >{{ siteName.lead }} <span class="brand-lockup__accent">{{ siteName.accent }}</span></span
            >
          </router-link>
          <!-- Same pale gold badge as "Premium" in the river lists -->
          <v-chip
            v-if="showTestingEnvironmentChip"
            class="ml-3 d-none d-sm-inline-flex qev-badge"
            color="gold-tint"
            variant="flat"
            size="small"
          >
            Testing environment
          </v-chip>
        </div>
      </v-toolbar-title>
      <top-nav-links :is-member="isMember" :is-admin="isAdmin"></top-nav-links>
      <admin-notifications-bell v-if="isAdmin"></admin-notifications-bell>
      <account-menu
        :is-connected="isConnected"
        :is-member="isMember"
        :is-admin="isAdmin"
        :user="user"
        @logout="logout"
        @toggle-admin-drawer="adminDrawer = !adminDrawer"
      ></account-menu>
    </v-app-bar>

    <v-main style="padding-bottom: var(--footer-height, 0px)">
      <main-navigation-drawer v-model="drawer" :app-version="appVersion"></main-navigation-drawer>
      <admin-navigation-drawer
        v-model="adminDrawer"
        :is-impersonating="isImpersonating"
        @update:navGroup="adminNavGroup = $event"
        @request-impersonation="startImpersonation"
        @restore-admin-session="restoreAdminSession"
      ></admin-navigation-drawer>
      <banner-fixed-slot :show="isImpersonating" :top="appBarHeight">
        <impersonation-banner
          :target-uid="impersonationTargetUid"
          @restore-admin-session="restoreAdminSession"
        ></impersonation-banner>
      </banner-fixed-slot>
      <banner-fixed-slot :show="showLoginBanner" :top="appBarHeight">
        <login-banner></login-banner>
      </banner-fixed-slot>
      <banner-fixed-slot :show="showSubscriptionBanner" :top="appBarHeight">
        <subscription-banner></subscription-banner>
      </banner-fixed-slot>
      <router-view v-slot="{ Component, route }">
        <keep-alive v-if="route.name === 'rivieres'" :include="keepAliveComponentNames" :max="3">
          <component
            :is="Component"
            :is-member="isMember"
            :is-admin="isAdmin"
            :is-connected="isConnected"
            :is-auth-resolved="authStateResolved"
            :membership-end-date="membershipEndDate"
          ></component>
        </keep-alive>
        <component
          :is="Component"
          v-else
          :is-member="isMember"
          :is-admin="isAdmin"
          :is-connected="isConnected"
          :is-auth-resolved="authStateResolved"
          :membership-end-date="membershipEndDate"
        ></component>
      </router-view>
    </v-main>
    <app-footer
      :app-version="appVersion"
      :is-member="isMember"
      :is-admin="isAdmin"
      :is-auth-resolved="authStateResolved"
    ></app-footer>
    <cookie-consent-banner @consent="handleCookieConsent"></cookie-consent-banner>
    <v-snackbar-queue v-model="notificationQueue.messages" location="top">
      <template #actions="{ item, props }">
        <v-btn
          v-if="item.actionText"
          variant="text"
          @click="
            item.onAction();
            props.onClick();
          "
        >
          {{ item.actionText }}
        </v-btn>
        <v-btn variant="text" v-bind="props">Fermer</v-btn>
      </template>
    </v-snackbar-queue>
  </v-app>
</template>

<script>
  import packageJson from "../package.json";
  import { auth, functions, setAnalyticsConsent } from "@/firebase";
  import { getAuthenticatedState, unauthenticatedState } from "@/services/AuthState.service";
  import AdminNavigationDrawer from "@/components/AdminNavigationDrawer.vue";
  import AccountMenu from "@/components/AccountMenu.vue";
  import AdminNotificationsBell from "@/components/AdminNotificationsBell.vue";
  import BannerFixedSlot from "@/components/BannerFixedSlot.vue";
  import AppFooter from "@/components/AppFooter.vue";
  import LoginBanner from "@/components/LoginBanner.vue";
  import MainNavigationDrawer from "@/components/MainNavigationDrawer.vue";
  import ImpersonationBanner from "@/components/ImpersonationBanner.vue";
  import SubscriptionBanner from "@/components/pages/subscription/SubscriptionBanner.vue";
  import TopNavLinks from "@/components/TopNavLinks.vue";
  import CookieConsentBanner from "@/components/CookieConsentBanner.vue";
  import { notificationQueue } from "@/services/Notification.service";
  import { getSiteName } from "@/services/SiteName.service";
  export default {
    name: "App",
    components: {
      AdminNavigationDrawer,
      AdminNotificationsBell,
      AccountMenu,
      AppFooter,
      BannerFixedSlot,
      ImpersonationBanner,
      LoginBanner,
      MainNavigationDrawer,
      SubscriptionBanner,
      TopNavLinks,
      CookieConsentBanner,
    },
    data: () => ({
      appVersion: packageJson.version,
      isConnected: false,
      isMember: false,
      membershipEndDate: null,
      isAdmin: false,
      user: null,
      searchCriterias: { regions: [], classes: [], levels: [] },
      drawer: false,
      adminDrawer: false,
      appBarHeight: 64,
      authStateUnsubscribe: null,
      authStateResolved: false,
      authStateRequestId: 0,
      isImpersonating: false,
      impersonationContext: null,
      notificationQueue,
    }),
    computed: {
      siteName() {
        return getSiteName();
      },
      showLoginBanner() {
        return this.authStateResolved && !this.isConnected;
      },
      showSubscriptionBanner() {
        return this.authStateResolved && this.isConnected && !this.isMember && !this.isAdmin && !this.isImpersonating;
      },
      keepAliveComponentNames() {
        return ["sections"];
      },
      showTestingEnvironmentChip() {
        return process.env.NODE_ENV !== "production";
      },
      layoutStateKey() {
        return `${this.isConnected}-${this.isMember}-${this.isAdmin}`;
      },
      impersonationTargetUid() {
        return this.impersonationContext ? this.impersonationContext.targetUid : "";
      },
    },
    async mounted() {
      window.addEventListener("resize", this.updateLayoutOffsets);
      this.authStateUnsubscribe = auth.onAuthStateChanged(async (user) => {
        const requestId = ++this.authStateRequestId;
        this.authStateResolved = false;

        try {
          if (user) {
            this.user = user;
            const tokenResult = await user.getIdTokenResult();
            this.syncImpersonationState(user, tokenResult.claims || {});
            const authState = await getAuthenticatedState(user);

            // Ignore late async responses from previous auth transitions.
            if (requestId !== this.authStateRequestId) {
              return;
            }

            this.applyAuthState(authState);
          } else {
            this.user = null;
            this.clearImpersonationContext();
            this.applyAuthState(unauthenticatedState);
          }
        } catch (error) {
          console.error("Error resolving authentication state:", error);
          this.applyAuthState(unauthenticatedState);
        } finally {
          if (requestId === this.authStateRequestId) {
            this.authStateResolved = true;
            this.refreshLayoutOnNextTick();
          }
        }
      });

      this.refreshLayoutOnNextTick();
    },
    beforeUnmount() {
      window.removeEventListener("resize", this.updateLayoutOffsets);
      if (this.authStateUnsubscribe) {
        this.authStateUnsubscribe();
        this.authStateUnsubscribe = null;
      }
    },
    methods: {
      applyAuthState(authState) {
        this.isConnected = authState.isConnected;
        this.isMember = authState.isMember;
        this.isAdmin = authState.isAdmin;
        if (Object.prototype.hasOwnProperty.call(authState, "membershipEndDate")) {
          this.membershipEndDate = authState.membershipEndDate;
        }
        if (Object.prototype.hasOwnProperty.call(authState, "searchCriterias")) {
          this.searchCriterias = authState.searchCriterias;
        }
      },
      refreshLayoutOnNextTick() {
        this.$nextTick(() => {
          this.updateLayoutOffsets();
        });
      },
      getElementHeight(elementRef, fallback = 0) {
        return elementRef ? elementRef.offsetHeight : fallback;
      },
      updateLayoutOffsets() {
        const appBarElement = this.$refs.appBar && this.$refs.appBar.$el ? this.$refs.appBar.$el : null;
        this.appBarHeight = this.getElementHeight(appBarElement, 64);
      },
      async logout() {
        this.clearImpersonationContext();
        await auth.signOut();
        this.applyAuthState(unauthenticatedState);

        if (this.$route.name != "accueil") this.$router.push({ name: "accueil" });
      },
      getStoredImpersonationContext() {
        const rawContext = window.localStorage.getItem("qwwImpersonationContext");
        if (!rawContext) {
          return null;
        }

        try {
          const parsedContext = JSON.parse(rawContext);
          if (
            parsedContext &&
            typeof parsedContext.adminUid === "string" &&
            typeof parsedContext.targetUid === "string"
          ) {
            return {
              adminUid: parsedContext.adminUid,
              targetUid: parsedContext.targetUid,
            };
          }
        } catch (error) {
          console.warn("Invalid impersonation context in storage:", error);
        }

        return null;
      },
      persistImpersonationContext(context) {
        this.impersonationContext = context;
        this.isImpersonating = true;
        window.localStorage.setItem("qwwImpersonationContext", JSON.stringify(context));
      },
      clearImpersonationContext() {
        this.impersonationContext = null;
        this.isImpersonating = false;
        window.localStorage.removeItem("qwwImpersonationContext");
      },
      syncImpersonationState(user, claims) {
        const claimHasImpersonation = claims.impersonation === true;
        const claimImpersonatorUid = typeof claims.impersonatorUid === "string" ? claims.impersonatorUid : "";

        if (claimHasImpersonation && claimImpersonatorUid) {
          this.persistImpersonationContext({
            adminUid: claimImpersonatorUid,
            targetUid: user.uid,
          });
          return;
        }

        const storedContext = this.getStoredImpersonationContext();
        if (storedContext && storedContext.adminUid === user.uid) {
          this.clearImpersonationContext();
          return;
        }

        if (storedContext && storedContext.targetUid === user.uid) {
          this.persistImpersonationContext(storedContext);
          return;
        }

        this.clearImpersonationContext();
      },
      async requestImpersonationToken(targetUid) {
        const createToken = functions.httpsCallable("adminCreateImpersonationToken");
        const response = await createToken({ targetUid });
        if (!response || !response.data || !response.data.token) {
          throw new Error("Invalid impersonation token response.");
        }
        return response.data.token;
      },
      async startImpersonation(targetUid) {
        const trimmedTargetUid = typeof targetUid === "string" ? targetUid.trim() : "";
        if (!trimmedTargetUid) {
          window.alert("Veuillez fournir un UID utilisateur valide.");
          return;
        }

        if (!this.user || !this.user.uid) {
          window.alert("Vous devez être connecté en tant qu'admin.");
          return;
        }

        try {
          const token = await this.requestImpersonationToken(trimmedTargetUid);
          this.persistImpersonationContext({
            adminUid: this.user.uid,
            targetUid: trimmedTargetUid,
          });
          await auth.signInWithCustomToken(token);
        } catch (error) {
          console.error("Unable to start impersonation:", error);
          window.alert("Impossible de démarrer l'impersonation. Vérifiez l'UID et vos permissions.");
        }
      },
      handleCookieConsent(accepted) {
        setAnalyticsConsent(accepted);
      },
      async restoreAdminSession() {
        const context = this.impersonationContext || this.getStoredImpersonationContext();
        if (!context || !context.adminUid) {
          window.alert("Aucune session d'impersonation active.");
          return;
        }

        try {
          const token = await this.requestImpersonationToken(context.adminUid);
          this.clearImpersonationContext();
          await auth.signInWithCustomToken(token);
        } catch (error) {
          console.error("Unable to restore admin session:", error);
          window.alert("Impossible de revenir à la session admin.");
        }
      },
    },
    watch: {
      layoutStateKey() {
        this.refreshLayoutOnNextTick();
      },
    },
  };
</script>

<style>
  :root {
    --footer-height: 0px;
  }

  .brand-lockup {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    color: inherit;
    text-decoration: none;
  }

  .brand-lockup__logo {
    display: block;
    flex: none;
  }

  .brand-lockup__name {
    font-size: 1.25rem;
    font-weight: 700;
    letter-spacing: -0.2px;
    white-space: nowrap;
  }

  .brand-lockup__accent {
    font-weight: 400;
    color: #8fd3e0;
  }

  .brand-lockup__mark {
    position: relative;
    display: block;
    flex: none;
  }

  /* Pale gold badge (.qev-badge colours) over the bottom of the logo, inside its box: the toolbar title clips overflow */
  .env-tag {
    position: absolute;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
    padding: 0 4px;
    border-radius: 999px;
    border: 1px solid rgba(var(--v-theme-gold), 0.7);
    background: rgb(var(--v-theme-gold-tint));
    color: rgb(var(--v-theme-on-gold-tint));
    font-size: 0.55rem;
    font-weight: 800;
    line-height: 1.3;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  /* Phones: smaller logo and name, so the menu and account buttons keep their room */
  @media (max-width: 600px) {
    .brand-lockup {
      gap: 8px;
    }

    .brand-lockup__logo {
      width: 32px;
      height: 32px;
    }

    .brand-lockup__name {
      font-size: 1.05rem;
    }
  }

  @media (max-width: 360px) {
    .brand-lockup__logo {
      width: 28px;
      height: 28px;
    }

    .brand-lockup__name {
      font-size: 0.95rem;
    }
  }

  .brand-lockup:focus-visible {
    outline: 2px solid #8fd3e0;
    outline-offset: 4px;
    border-radius: 4px;
  }
</style>
